export default `# The Movie DB API

At this point, with your boilerplate up and running, you are ready to start building the *Sweet Pumpkins* application.

## What Are We Building?

In this course, we're going to build a web application that will help you find the next movie to watch.

To browse movies, we will use [The Movie DB](https://www.themoviedb.org/about) API.
Our application will display a collection of movies based on the set of criteria.

## The Movie DB

The Movie Database (TMDb) is a community-built movie and TV shows database.

It's a huge collection of over 350 thousand of movies and over 70 thousand of TV shows.
Over 1 million of people have contributed to the database.

## How The API Works

How does the application communicate with external resources?

In the majority of cases, the communication happens via REST.

REST stands for a Representational State Transfer. Simply put, REST is a way of talking to another resource on the internet to access its data.

REST uses a handful of methods that allow to **C**reate, **R**ead, **U**pdate and **D**elete data remotely. These methods are called CRUD operations.

Such external resource that has been made available somewhere on the internet for you to talk to is called an API.

Imagine that you run a movie library. This is a database.

You work at the front desk and you are responsible for answering all the questions (requests) about the movies. You are an API.

When a customer wants to get a movie, he or she fills out a GET form with his or her the request and hands it over to you. This is a GET request.

If the customer wants to take out the movie from the library, he or she fills out a DELETE form and hands it over to you.

If he or she wants to add a new movie to the library, he or she fills out a POST form and gives it to you.

Once you receive the request, you have to check if the customer is authorised to interact with your movie library in the first place.

To save time spent on servicing too many requests from random customers you can give an access password only to your trusted customers. It's called an API key.

An API key is a secret string, for example: *15392ad46022d1ae658063b443c99784*.

Now, you have an easy way to verify the requests.
If a customer gives you a form with a valid API key written at the top of the form, you can be sure that he or she is allowed to interact with the movie library.
The customer is *authenticated*.

If you receive a request without a valid API key, you can decline it. You will hand the form back to the customer with a short note in red: "Not authorised" or "Invalid API key".

When an authorised customer gives you a GET form with a specific movie title you will find it and bring it back to him. You will also add a note in blue
on top of the form "Request OK".

This is how a communication between a web application and an external API works.

## Getting an API Key

Since we want to interact with the TMDB API, we need an API key first.

Go and sign up [here](https://www.themoviedb.org/account/signup).

Once you've signed up and confirmed your email address, log in to your account and go to the settings page:

![TMDB Settings page](https://sweetpumpkins.codecamps.com/settings-min.jpg)

Click on the *Create* tab on the *API* page and choose *Developer*:

![Get API key](https://sweetpumpkins.codecamps.com/get-api-key-min.jpg)

Accept the *Terms and Conditions* and fill out the form. You don't have a valid URL in the *Application URL*, but – for now – you can put the \`localhost:3000\` there:

![API form](https://sweetpumpkins.codecamps.com/api-form-min.jpg)

When you submit the form you will be redirected to another page with an API key waiting for you.

Remember not to store your API key in a file that you will push to *Github* repository.

## Where to Store Your API key?

When Node starts the server, it creates a global object called a \`process\`.

One of the \`process\` object's properties is called \`env\` which stands for an environment.
The \`env\` is a perfect place to store environment variables, such as API keys.

There are two ways how to assign a value to the *environment variable* with the *create-react-app*.

The *create-react-app* recognises only variables that start with the \`REACT_APP_\` keyword.

Let's create a \`REACT_APP_TMDB_API_KEY\` variable that will store the TMDB API key.

The first way is to pass the variable in the terminal when starting the server.

on Windows (cmd.exe):
\`\`\`plainJavascript
set REACT_APP_TMDB_API_KEY=my_api_key&&yarn start
\`\`\`

on macOS:
\`\`\`plainJavascript
REACT_APP_TMDB_API_KEY=1111aaaa yarn start
\`\`\`

The second and easier way is to create an *.env* file in the root directory of the project and add the following line inside:
\`\`\`plainJavascript
REACT_APP_TMDB_API_KEY=my_api_key
\`\`\`

Let's go for the second solution. Replace *my_api_key* with your API key.

Next, add *.env* to the list of ignored files in *.gitignore* file.

Otherwise, if you use *Github*, you will push the API key to the public repository.
Open the *.gitignore* file and list the *.env* file in a new line:
\`\`\`plainJavascript
# misc
.env
.DS_Store
.env.local
.env.development.local
.env.test.local
.env.production.local
\`\`\`

Now, let's find out how to access the environment variable in the application.

The \`process\` is a global variable. Which means that you can access it anywhere in the application.

Let's \`console.log\` it from the render method of the \`App\` component. But first, let's delete the *App.css* and
replace the \`App\` component in the \`App.js\` file with a simpler version:

\`\`\`plainJsx
import React from 'react';

const App = () => {
  console.log('API key:', process.env.REACT_APP_TMDB_API_KEY);
  return (
    <div>
      <h1>Sweet Pumpkins</h1>
    </div>
  )
}

export default App;
\`\`\`

Restart the server in the terminal window. Now, when you check the *console* in Developer Tools, you should see the API key logged out.

## Interacting With an API

When you want to interact with an API, you need to know its URL.
And you have to understand how to ask questions. Questions are called *queries*.

Let's go to the TMDB API documentation and inspect one of the API *endpoints*. The *endpoint* is an URL prepared to answer a very specific question.
Open the docs for the [discover movies endpoint](https://developers.themoviedb.org/3/discover/movie-discover).

The first thing you should notice is the word \`GET\` in green font. This endpoint will only accept \`GET\` requests – requests to get data.
You can't update, create or delete data using the \`GET\` endpoint.

Next, to the \`GET\` word, there is the *endpoint* address: \`/discover/movie\`. But we are still missing the API's URL.
Click on the \`Try it out\`

![API try it out tab](https://sweetpumpkins.codecamps.com/api-try-it-out.jpg)

and scroll to the bottom. Here you should find a really long URL string, similar to this one:
\`\`\`plainJsx
https://api.themoviedb.org/3/discover/movie?api_key=<<api_key>>&language=en-US&sort_by=popularity.desc&include_adult=false&include_video=false&page=1
\`\`\`

Let's deconstruct it. It consists of several parts:
1. https://api.themoviedb.org/3/ – this is an API URL version 3
2. /discover/movie - that's the *endpoint* address
3. The \`?\` is followed by a set of query arguments separated by \`&\`

The first query argument is the \`api_key\`.
It's followed by key-value pairs. For example: \`language=en-US\`.

To boil it down, an API query has the following structure:
\`\`\`plainJsx
Query_URL/Endpoint_address?api_key&query_first_argument&...&query_last_argument
\`\`\`

Copy the whole string and paste it into your browser's address bar and then replace the \`<<api_key>>\` with your API key and press enter.

Instead of the nicely displayed website, you should see something similar to this:

![API unformatted JSON response](https://sweetpumpkins.codecamps.com/API-unformatted-json.jpg)

The API has just sent you a reply. It's a JSON object (JavaScript Object Notation) turned into a string (stringified).
JSON objects are used to pass the information around the web.

A regular JSON object looks like the one below:
\`\`\`plainJsx
{
  "page": 1,
  "results": [
    {
      "vote_count": 3122,
      "release_date": "2017-09-05"
    },
    {
      "vote_count": 491,
      "release_date": "2012-01-01"
    }
  ]
}
\`\`\`

The object above has two attributes: \`page\` and \`results\`. The \`page\` is a number. The \`results\` attribute is an array of objects that have
\`vote_count\` and \`release_count\` attributes.

The JSON object can have hundreds of attributes and can be nested many levels down.

Let's go back to our unreadable results sent by the TMDB API.

Install a Chrome extension [JSON Formatter](https://chrome.google.com/webstore/search/json%20formatter) and reload the page with the query.
Now, the JSON Formatter will take care of formatting the JSON object and displaying it in a more readable way:

![API formatted JSON response](https://sweetpumpkins.codecamps.com/API-formatted-json.jpg)

In the "Sweet Pumpkins" application, you will use this exact endpoint to get information about the movies.
You now know the API URL, the endpoint's address and you have the API key.

First, you are going to learn how to use exactly the same query as above to get a list of movies. In the next step, you are going to discover how to build queries to get a list of movies you want.
`;



// WEBPACK FOOTER //
// ./src/book/chapters/tmdb-api.js